import type { CSSProperties, ReactNode } from 'react';
import type { FitViewOptions } from '../../core';
import { useCustomMindmap } from '../../hooks';
import { selectIsInteractive, selectViewport, selectZoomLimits, useMindmapStore, useStoreApi } from '../../store';
import { classNames } from '../../utils';
import { ControlButton } from './ControlButton';
import { FitViewIcon, LockIcon, MinusIcon, PlusIcon } from './icons';
import { ZoomLevelInput } from './ZoomLevelInput';

export type ControlsProps = {
  showZoom?: boolean;
  showFitView?: boolean;
  showInteractive?: boolean;
  /** Shows the zoom level between zoom out and zoom in, as a field: type a percentage to zoom there. */
  showZoomLevel?: boolean;
  /** Stack direction of the buttons (v11). */
  orientation?: 'vertical' | 'horizontal';
  fitViewOptions?: FitViewOptions;
  className?: string;
  style?: CSSProperties;
  /** Extra buttons (e.g. `<ControlButton>`), after the built-in ones (v11). */
  children?: ReactNode;
};

const Divider = () => <span className="cm-controls-divider" aria-hidden="true" />;

/**
 * v11 `<Controls>`: zoom, fit view, the interactivity lock and any `children`, in the bottom-left corner.
 * Horizontal with `showZoomLevel` it is the zoom bar of the redesign: − 100% + | fit, lock | children.
 */
export const Controls = ({
  showZoom = true,
  showFitView = true,
  showInteractive = true,
  showZoomLevel = false,
  orientation = 'vertical',
  fitViewOptions,
  className,
  style,
  children,
}: ControlsProps) => {
  const instance = useCustomMindmap();
  const store = useStoreApi();
  const { zoom } = useMindmapStore(selectViewport);
  const { minZoom, maxZoom } = useMindmapStore(selectZoomLimits, (a, b) => a.minZoom === b.minZoom && a.maxZoom === b.maxZoom);
  const isInteractive = useMindmapStore(selectIsInteractive);
  const isBar = orientation === 'horizontal';

  const zoomIn = (
    <ControlButton title="zoom in" name="zoomin" onClick={() => instance.zoomIn()} disabled={zoom >= maxZoom}>
      <PlusIcon />
    </ControlButton>
  );
  const zoomOut = (
    <ControlButton title="zoom out" name="zoomout" onClick={() => instance.zoomOut()} disabled={zoom <= minZoom}>
      <MinusIcon />
    </ControlButton>
  );
  const hasViewGroup = showFitView || showInteractive;

  return (
    <div className={classNames('cm-panel', 'cm-controls', orientation, 'bottom', 'left', className)} style={style}>
      {showZoom && showZoomLevel && (
        <>
          {zoomOut}
          <ZoomLevelInput zoom={zoom} minZoom={minZoom} maxZoom={maxZoom} onZoom={(level) => instance.zoomTo(level)} />
          {zoomIn}
        </>
      )}
      {showZoom && !showZoomLevel && (
        <>
          {zoomIn}
          {zoomOut}
        </>
      )}
      {isBar && showZoom && hasViewGroup && <Divider />}
      {showFitView && (
        <ControlButton title="fit view" name="fitview" onClick={() => instance.fitView(fitViewOptions)}>
          <FitViewIcon />
        </ControlButton>
      )}
      {showInteractive && (
        <ControlButton title="toggle interactivity" name="interactive" onClick={() => store.getState().setInteractive(!isInteractive)}>
          <LockIcon isOpen={isInteractive} />
        </ControlButton>
      )}
      {isBar && children && (showZoom || hasViewGroup) && <Divider />}
      {children}
    </div>
  );
};
